import React, { useEffect, useState } from 'react';
import api from '../../api';
import { Loader } from '../../components/Shared';
import { useToast } from '../../context/AppContext';
import { useAuth } from '../../context/AppContext';

// Moved outside the component so React keeps a stable reference across re-renders
function Field({ label, name, type = 'text', placeholder, rows, value, onChange, settings, set }) {
  return (
    <div className="mb-3">
      <label className="form-label-styled">{label}</label>
      {type === 'textarea' ? (
        <textarea className="form-control-styled" rows={rows || 3} placeholder={placeholder}
          value={value !== undefined ? value : (settings[name] || '')}
          onChange={onChange || (e => set(name, e.target.value))} />
      ) : (
        <input type={type} className="form-control-styled" placeholder={placeholder}
          value={value !== undefined ? value : (settings[name] || '')}
          onChange={onChange || (e => set(name, e.target.value))} />
      )}
    </div>
  );
}

export default function AdminSettingsPage() {
  const [settings, setSettings]       = useState({});
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [activeTab, setActiveTab]     = useState('general');

  // Change email state
  const [emailForm, setEmailForm]     = useState({ new_email: '', password: '' });
  const [emailSaving, setEmailSaving] = useState(false);

  // Change password state
  const [pwForm, setPwForm]           = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwSaving, setPwSaving]       = useState(false);

  const { addToast } = useToast();
  const { user, updateUser } = useAuth();

  useEffect(() => {
    api.get('/settings').then(r => setSettings(r.data.data || {})).finally(() => setLoading(false));
  }, []);

  const set  = (k, v) => setSettings(s => ({ ...s, [k]: v }));

  const saveSettings = async e => {
    e.preventDefault(); setSaving(true);
    try { await api.put('/settings', settings); addToast('Settings saved!', 'success'); }
    catch { addToast('Failed to save settings', 'error'); }
    finally { setSaving(false); }
  };

  const changeEmail = async e => {
    e.preventDefault(); setEmailSaving(true);
    try {
      const res = await api.post('/auth/change-email', emailForm);
      // Update stored token with new one
      if (res.data.data?.token) {
        localStorage.setItem('bc_token', res.data.data.token);
        updateUser({ email: res.data.data.new_email });
      }
      addToast('Email updated successfully!', 'success');
      setEmailForm({ new_email: '', password: '' });
    } catch (err) {
      addToast(err.response?.data?.message || 'Failed to update email', 'error');
    } finally { setEmailSaving(false); }
  };

  const changePassword = async e => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm_password) {
      addToast('New passwords do not match', 'error'); return;
    }
    if (pwForm.new_password.length < 6) {
      addToast('Password must be at least 6 characters', 'error'); return;
    }
    setPwSaving(true);
    try {
      await api.post('/auth/change-password', { current_password: pwForm.current_password, new_password: pwForm.new_password });
      addToast('Password changed successfully!', 'success');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      addToast(err.response?.data?.message || 'Failed to change password', 'error');
    } finally { setPwSaving(false); }
  };

  if (loading) return <Loader />;

  const tabs = [
    { id: 'general',  label: 'General',    icon: 'bi-house' },
    { id: 'contact',  label: 'Contact',    icon: 'bi-telephone' },
    { id: 'social',   label: 'Social',     icon: 'bi-share' },
    { id: 'stats',    label: 'Stats',      icon: 'bi-bar-chart' },
    { id: 'account',  label: 'My Account', icon: 'bi-person-gear' },
  ];

  return (
    <div>
      <div className="mb-4">
        <h2 style={{ fontFamily: 'var(--font-display)', color: 'var(--text)', marginBottom: 4 }}>Settings</h2>
        <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '0.875rem' }}>Manage your website configuration and account</p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '2px solid var(--border)', paddingBottom: 0, flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '10px 18px',
              fontSize: '0.875rem', fontWeight: 600, fontFamily: 'var(--font-body)',
              color: activeTab === t.id ? 'var(--c4-dark)' : 'var(--text-muted)',
              borderBottom: activeTab === t.id ? '2px solid var(--c4)' : '2px solid transparent',
              marginBottom: -2, transition: 'var(--transition)',
              display: 'flex', alignItems: 'center', gap: 7,
            }}>
            <i className={`bi ${t.icon}`} />{t.label}
          </button>
        ))}
      </div>

      {/* ── General Tab ─────────────────────────────────── */}
      {activeTab === 'general' && (
        <form onSubmit={saveSettings}>
          <div className="admin-card p-4 mb-4">
            <h5 style={{ color: 'var(--c4-dark)', fontFamily: 'var(--font-display)', marginBottom: 22, fontSize: '1rem' }}>Brand & Identity</h5>
            <div className="row g-3">
              <div className="col-sm-6"><Field label="Site Name" name="site_name" placeholder="Bacha & Son Construction" settings={settings} set={set} /></div>
              <div className="col-sm-6"><Field label="Tagline" name="site_tagline" placeholder="Building Excellence, Crafting Legacies" settings={settings} set={set} /></div>
              <div className="col-12"><Field label="About / Company Description" name="site_about" type="textarea" rows={4} placeholder="Company description shown on Home & About pages..." settings={settings} set={set} /></div>
              <div className="col-12"><Field label="Meta Description (SEO)" name="meta_description" type="textarea" rows={2} placeholder="Website meta description for search engines..." settings={settings} set={set} /></div>
            </div>
          </div>
          <button type="submit" className="btn-primary-styled" disabled={saving}>
            {saving ? 'Saving...' : <><i className="bi bi-check2 me-1" />Save Settings</>}
          </button>
        </form>
      )}

      {/* ── Contact Tab ─────────────────────────────────── */}
      {activeTab === 'contact' && (
        <form onSubmit={saveSettings}>
          <div className="admin-card p-4 mb-4">
            <h5 style={{ color: 'var(--c4-dark)', fontFamily: 'var(--font-display)', marginBottom: 22, fontSize: '1rem' }}>Contact Information</h5>
            <div className="row g-3">
              <div className="col-sm-6"><Field label="Contact Email" name="site_email" type="email" placeholder="info@company.com" settings={settings} set={set} /></div>
              <div className="col-sm-6"><Field label="Phone Number" name="site_phone" placeholder="+1 (555) 000-0000" settings={settings} set={set} /></div>
              <div className="col-12"><Field label="Address" name="site_address" type="textarea" rows={2} placeholder="123 Main St, City, Country" settings={settings} set={set} /></div>
            </div>
          </div>
          <button type="submit" className="btn-primary-styled" disabled={saving}>
            {saving ? 'Saving...' : <><i className="bi bi-check2 me-1" />Save Settings</>}
          </button>
        </form>
      )}

      {/* ── Social Tab ──────────────────────────────────── */}
      {activeTab === 'social' && (
        <form onSubmit={saveSettings}>
          <div className="admin-card p-4 mb-4">
            <h5 style={{ color: 'var(--c4-dark)', fontFamily: 'var(--font-display)', marginBottom: 22, fontSize: '1rem' }}>Social Media Links</h5>
            <Field label="Facebook URL"    name="facebook_url"  placeholder="https://facebook.com/yourpage" settings={settings} set={set} />
            <Field label="Instagram URL"   name="instagram_url" placeholder="https://instagram.com/yourhandle" settings={settings} set={set} />
            <Field label="LinkedIn URL"    name="linkedin_url"  placeholder="https://linkedin.com/company/yourco" settings={settings} set={set} />
            <Field label="Twitter / X URL" name="twitter_url"   placeholder="https://twitter.com/yourhandle" settings={settings} set={set} />
          </div>
          <button type="submit" className="btn-primary-styled" disabled={saving}>
            {saving ? 'Saving...' : <><i className="bi bi-check2 me-1" />Save Settings</>}
          </button>
        </form>
      )}

      {/* ── Stats Tab ───────────────────────────────────── */}
      {activeTab === 'stats' && (
        <form onSubmit={saveSettings}>
          <div className="admin-card p-4 mb-4">
            <h5 style={{ color: 'var(--c4-dark)', fontFamily: 'var(--font-display)', marginBottom: 8, fontSize: '1rem' }}>Homepage Statistics</h5>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 22 }}>These numbers appear in the animated stats section on the homepage.</p>
            <div className="row g-3">
              <div className="col-sm-6"><Field label="Years of Experience" name="site_years_experience" type="number" placeholder="25" settings={settings} set={set} /></div>
              <div className="col-sm-6"><Field label="Projects Completed"  name="site_projects_completed" type="number" placeholder="500" settings={settings} set={set} /></div>
              <div className="col-sm-6"><Field label="Happy Clients"       name="site_happy_clients" type="number" placeholder="350" settings={settings} set={set} /></div>
              <div className="col-sm-6"><Field label="Team Members"        name="site_team_members" type="number" placeholder="120" settings={settings} set={set} /></div>
            </div>
          </div>
          <button type="submit" className="btn-primary-styled" disabled={saving}>
            {saving ? 'Saving...' : <><i className="bi bi-check2 me-1" />Save Settings</>}
          </button>
        </form>
      )}

      {/* ── Account Tab ─────────────────────────────────── */}
      {activeTab === 'account' && (
        <div className="row g-4">
          {/* Current account info */}
          <div className="col-12">
            <div className="admin-card p-4">
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg,var(--c3),var(--c4))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: 700, color: 'var(--text)', fontSize: '1rem' }}>{user?.name}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>{user?.email}</div>
                  <span className="badge-gold-light mt-1 d-inline-block">{user?.role}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Change Email */}
          <div className="col-lg-6">
            <div className="admin-card p-4">
              <h5 style={{ color: 'var(--c4-dark)', fontFamily: 'var(--font-display)', marginBottom: 8, fontSize: '1rem' }}>
                <i className="bi bi-envelope me-2" />Change Email Address
              </h5>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: 20 }}>
                Current: <strong>{user?.email}</strong>.
              </p>
              <form onSubmit={changeEmail}>
                <div className="mb-3">
                  <label className="form-label-styled">New Email Address</label>
                  <input type="email" className="form-control-styled" required placeholder="new@email.com"
                    value={emailForm.new_email}
                    onChange={e => setEmailForm(f => ({ ...f, new_email: e.target.value }))} />
                </div>
                <div className="mb-4">
                  <label className="form-label-styled">Confirm with Current Password</label>
                  <input type="password" className="form-control-styled" required placeholder="••••••••"
                    value={emailForm.password}
                    onChange={e => setEmailForm(f => ({ ...f, password: e.target.value }))} />
                </div>
                <button type="submit" className="btn-primary-styled" disabled={emailSaving}>
                  {emailSaving
                    ? <><div style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.6s linear infinite', display: 'inline-block', marginRight: 6 }} />Updating...</>
                    : <><i className="bi bi-check2 me-1" />Update Email</>}
                </button>
              </form>
            </div>
          </div>

          {/* Change Password */}
          <div className="col-lg-6">
            <div className="admin-card p-4">
              <h5 style={{ color: 'var(--c4-dark)', fontFamily: 'var(--font-display)', marginBottom: 8, fontSize: '1rem' }}>
                <i className="bi bi-lock me-2" />Change Password
              </h5>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: 20 }}>
                Must be at least 6 characters. Use a strong password.
              </p>
              <form onSubmit={changePassword}>
                <div className="mb-3">
                  <label className="form-label-styled">Current Password</label>
                  <input type="password" className="form-control-styled" required placeholder="••••••••"
                    value={pwForm.current_password}
                    onChange={e => setPwForm(f => ({ ...f, current_password: e.target.value }))} />
                </div>
                <div className="mb-3">
                  <label className="form-label-styled">New Password</label>
                  <input type="password" className="form-control-styled" required placeholder="••••••••" minLength={6}
                    value={pwForm.new_password}
                    onChange={e => setPwForm(f => ({ ...f, new_password: e.target.value }))} />
                </div>
                <div className="mb-4">
                  <label className="form-label-styled">Confirm New Password</label>
                  <input type="password" className="form-control-styled" required placeholder="••••••••"
                    value={pwForm.confirm_password}
                    onChange={e => setPwForm(f => ({ ...f, confirm_password: e.target.value }))}
                    style={{ borderColor: pwForm.confirm_password && pwForm.new_password !== pwForm.confirm_password ? 'var(--danger)' : undefined }} />
                  {pwForm.confirm_password && pwForm.new_password !== pwForm.confirm_password && (
                    <div style={{ color: 'var(--danger)', fontSize: '0.78rem', marginTop: 5 }}>
                      <i className="bi bi-exclamation-circle me-1" />Passwords do not match
                    </div>
                  )}
                </div>
                <button type="submit" className="btn-primary-styled" disabled={pwSaving}>
                  {pwSaving
                    ? <><div style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.6s linear infinite', display: 'inline-block', marginRight: 6 }} />Changing...</>
                    : <><i className="bi bi-lock me-1" />Change Password</>}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
